/* AP Navigation — Mobile Drawer (3-level) */

class ApMobileNav {
  constructor(drawer) {
    this.drawer = drawer;
    this.trigger = document.querySelector('[data-ap-nav-trigger]');
    this.overlay = drawer.querySelector('.ap-mobile-nav__overlay');
    this.mainPanel = drawer.querySelector('.ap-mobile-nav__panel--main');
    this._history = []; // stack of panel IDs for back navigation

    this._onKeyDown = this._onKeyDown.bind(this);
    this._init();
  }

  _init() {
    this.trigger?.addEventListener('click', () => this.open());
    this.overlay?.addEventListener('click', () => this.close());

    // Close buttons (all levels)
    this.drawer.querySelectorAll('.ap-mobile-nav__close').forEach(btn => {
      btn.addEventListener('click', () => this.close());
    });

    // Back buttons (all levels) — pop history stack
    this.drawer.querySelectorAll('.ap-mobile-nav__back').forEach(btn => {
      btn.addEventListener('click', () => this._goBack());
    });

    // Level 1 → 2 triggers (on main panel)
    this.mainPanel?.querySelectorAll('[data-sub-panel]').forEach(btn => {
      btn.addEventListener('click', () => this._pushPanel(btn.dataset.subPanel));
    });

    // Level 2 → 3 triggers (on sub panels)
    this.drawer.querySelectorAll('.ap-mobile-nav__panel--sub [data-sub-sub-panel]').forEach(btn => {
      btn.addEventListener('click', () => this._pushPanel(btn.dataset.subSubPanel));
    });
  }

  open() {
    clearTimeout(this._closeResetTimer);
    this._resetAll();
    this._history = [];
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('ap-nav-open');
    this.trigger?.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this._onKeyDown);
    requestAnimationFrame(() => {
      this.drawer.querySelector('.ap-mobile-nav__close')?.focus({ preventScroll: true });
    });
  }

  // The panels container is overflow:hidden; focusing an element that is still
  // sliding in lets the browser scroll it sideways, shifting every panel. Pin
  // the horizontal scroll back to 0 to undo that.
  _resetScroll() {
    const panels = this.drawer.querySelector('.ap-mobile-nav__panels');
    if (panels) panels.scrollLeft = 0;
  }

  close() {
    this._history = [];
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('ap-nav-open');
    this.trigger?.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this._onKeyDown);
    // Wait for the slide-out transition to finish before resetting panel
    // state, so the active panel exits to the left with the drawer instead
    // of snapping back to its default off-screen position immediately.
    this._closeResetTimer = setTimeout(() => this._resetAll(), 260);
    this.trigger?.focus();
  }

  // Push a new panel onto the history stack and show it
  _pushPanel(panelId) {
    const current = this._currentPanelId();
    this._history.push(current);

    if (current === 'main') {
      // Hide main if leaving it — slides out left
      this.mainPanel?.classList.add('is-hidden');
    } else {
      // Pushing a sub-sub panel on top of a sub panel: slide the sub panel
      // out to the left (instead of its default right) so both panels sweep
      // across together with no gap, matching the main <-> sub transition.
      this.drawer.querySelector(`[data-panel-id="${current}"]`)?.classList.add('is-pushed-back');
    }

    // Activate target panel
    const panel = this.drawer.querySelector(`[data-panel-id="${panelId}"]`);
    if (!panel) return;
    panel.classList.add('is-active');

    requestAnimationFrame(() => {
      panel.querySelector('.ap-mobile-nav__back')?.focus({ preventScroll: true });
      this._resetScroll();
    });
  }

  // Go back one level using the history stack
  _goBack() {
    const currentId = this._currentPanelId();
    const prevId = this._history.pop();

    // Current panel exits: slides right (default off-screen position)
    if (currentId !== 'main') {
      this.drawer.querySelector(`[data-panel-id="${currentId}"]`)?.classList.remove('is-active', 'is-pushed-back');
    }

    if (!prevId || prevId === 'main') {
      // Return to main panel — slides back in from the left
      this.mainPanel?.classList.remove('is-hidden');
      requestAnimationFrame(() => {
        this.mainPanel?.querySelector('.ap-mobile-nav__close')?.focus({ preventScroll: true });
        this._resetScroll();
      });
    } else {
      // Return to previous sub panel — slides back in from the left
      const prevPanel = this.drawer.querySelector(`[data-panel-id="${prevId}"]`);
      prevPanel?.classList.remove('is-pushed-back');
      prevPanel?.classList.add('is-active');
      requestAnimationFrame(() => {
        prevPanel?.querySelector('.ap-mobile-nav__back')?.focus({ preventScroll: true });
        this._resetScroll();
      });
    }
  }

  _currentPanelId() {
    const active = this.drawer.querySelector(
      '.ap-mobile-nav__panel--sub.is-active:not(.is-pushed-back), .ap-mobile-nav__panel--sub-sub.is-active'
    );
    return active?.dataset.panelId || 'main';
  }

  _resetAll() {
    this.mainPanel?.classList.remove('is-hidden');
    this.drawer.querySelectorAll(
      '.ap-mobile-nav__panel--sub, .ap-mobile-nav__panel--sub-sub'
    ).forEach(p => p.classList.remove('is-active', 'is-pushed-back'));
  }

  _onKeyDown(e) {
    if (e.key === 'Escape') this.close();
  }
}

function initApNav() {
  const drawers = document.querySelectorAll('#ap-mobile-nav-drawer');
  if (!drawers.length) return;

  // If a section reload left a stale portaled copy, the freshly-rendered
  // in-section drawer comes first in DOM order — keep it, drop the rest.
  const drawer = drawers[0];
  drawers.forEach(d => { if (d !== drawer) d.remove(); });

  // Portal the drawer to <body> so its position:fixed resolves against the
  // viewport. App embeds (PageFly/UpCart/etc.) can put a transform/filter on
  // an ancestor, which would otherwise make `inset: 0` map to that ancestor's
  // box — shifting the drawer/overlay off the viewport.
  if (drawer.parentElement !== document.body) {
    document.body.appendChild(drawer);
  }

  drawer._apNavInit = new ApMobileNav(drawer);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApNav);
} else {
  initApNav();
}

document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('#ap-mobile-nav-drawer')) {
    initApNav();
  }
});
